import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';

import Landing from './marketing/Landing';
import Privacy from './marketing/Privacy';
import SignUp from './auth/SignUp';
import LogIn from './auth/LogIn';
import AuthCallback from './auth/AuthCallback';

import { ProfileProvider } from './account/ProfileContext';
import AccountShell from './account/AccountShell';
import Hub from './account/Hub';
import ProfileEditor from './account/ProfileEditor';
import NewGame from './account/NewGame';
import Clubs from './account/Clubs';
import News from './account/News';
import Settings from './account/Settings';
import Saves from './account/Saves';
import Achievements from './account/Achievements';

import GameShell from './game/GameShell';
import Home from './game/Home';
import Squad from './game/Squad';

import { bg, tx, font } from './theme';

function Loading() {
  return (
    <div style={{
      minHeight: '100vh', background: bg.base,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: font.display, fontSize: 12, letterSpacing: '0.24em',
      textTransform: 'uppercase', color: tx.muted,
    }}>
      Loading
    </div>
  );
}

// Layout route for everything that requires a logged-in user. Provides the
// DoF profile to the whole authed area (hub + in-club screens).
function ProtectedLayout() {
  const { session, loading } = useAuth();
  if (loading) return <Loading />;
  if (!session) return <Navigate to="/login" replace />;
  return (
    <ProfileProvider>
      <Outlet />
    </ProfileProvider>
  );
}

// Keeps logged-in users away from the marketing and auth pages.
function PublicOnly({ children }) {
  const { session, loading } = useAuth();
  if (loading) return <Loading />;
  if (session) return <Navigate to="/hub" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
          <Route path="/signup" element={<PublicOnly><SignUp /></PublicOnly>} />
          <Route path="/login" element={<PublicOnly><LogIn /></PublicOnly>} />

          {/* Privacy policy — open to everyone, logged in or not. */}
          <Route path="/privacy" element={<Privacy />} />

          {/* Magic-link and email-confirmation landing point.
              Must not be wrapped in PublicOnly — the session arrives mid-render. */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Authed area */}
          <Route element={<ProtectedLayout />}>
            {/* Account hub — the landing spot after login */}
            <Route path="/hub" element={<AccountShell />}>
              <Route index element={<Hub />} />
              <Route path="clubs" element={<Clubs />} />
              <Route path="news" element={<News />} />
              <Route path="saves" element={<Saves />} />
              <Route path="achievements" element={<Achievements />} />
              <Route path="settings" element={<Settings />} />
              <Route path="profile" element={<ProfileEditor />} />
              <Route path="new" element={<NewGame />} />
            </Route>

            {/* In-club experience, one per save */}
            <Route path="/game/:saveId" element={<GameShell />}>
              <Route index element={<Home />} />
              <Route path="squad" element={<Squad />} />
            </Route>
            <Route path="/game" element={<Navigate to="/hub" replace />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
